
/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { safeQuery, queryWithTimeout } from '../utils/supabaseHelpers';
import { Page } from '../types';

interface AuthContextType {
    isAuthenticated: boolean;
    isAuthChecking: boolean;
    userEmail: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// List of Super Admins from environment variable - Moved inside for testability
// const SUPER_ADMIN_EMAILS = (import.meta.env.VITE_SUPER_ADMIN_EMAILS || '').split(',').filter(Boolean);

interface AuthProviderProps {
    children: ReactNode;
    currentPage: Page;
    onNavigate: (page: Page) => void;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, currentPage, onNavigate }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isAuthChecking, setIsAuthChecking] = useState(true);
    const [userEmail, setUserEmail] = useState<string | null>(null);

    useEffect(() => {
        const checkUserAuthorization = async (email: string) => {
            const superAdmins = (import.meta.env.VITE_SUPER_ADMIN_EMAILS || '').split(',').filter(Boolean);

            // 1. Check Super Admin list first (fastest)
            if (superAdmins.includes(email)) {
                // if (shouldLog) console.log("✅ User is Super Admin:", email);
                return true;
            }

            // 2. Check admin_users table with robust query
            const { data, error } = await safeQuery(
                async () => supabase
                    .from('admin_users')
                    .select('id')
                    .eq('email', email)
                    .maybeSingle(),
                { timeout: 5000, retry: true }
            );

            if (error) {
                if (import.meta.env.DEV) console.error("❌ checkUserAuthorization error:", error);
                // If it's a network error, returning false logs them out. 
                return false;
            }

            if (data) {
                // if (shouldLog) console.log("✅ User is Authorized Admin:", email);
                return true;
            }

            // if (shouldLog) console.warn("⛔ User not found in admin_users or super admins:", email);
            return false;
        };

        const initAuth = async () => {
            try {
                // Use queryWithTimeout for robust handling
                const sessionResult = await queryWithTimeout(
                    supabase.auth.getSession(),
                    5000 // 5s timeout
                ).catch(err => {
                    console.warn("App auth check timed out", err);
                    return { data: { session: null }, error: err };
                });

                if (sessionResult.error) {
                    console.warn("App auth/session error:", sessionResult.error);
                }

                const { data: { session } } = sessionResult;

                if (session?.user) {
                    setUserEmail(session.user.email || null);
                    // Check authorization with timeout too
                    const isAuth = await checkUserAuthorization(session.user.email || '');
                    setIsAuthenticated(isAuth);
                    if (isAuth) {
                        // if (import.meta.env.DEV) console.log("Redirecting to dashboard from initAuth");
                        if (currentPage === Page.ADMIN_LOGIN) {
                            onNavigate(Page.ADMIN_DASHBOARD);
                        }
                    }
                }
            } catch (err) {
                console.warn('Auth init failed:', err);
                // Fallback: Proceed as unauthenticated
                setIsAuthenticated(false);
            } finally {
                setIsAuthChecking(false);
            }
        };
        initAuth();

        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
            // if (import.meta.env.DEV) console.log("🔐 Auth State Change:", event, session?.user?.email);

            if (session?.user) {
                setUserEmail(session.user.email || null);
                const isAuth = await checkUserAuthorization(session.user.email || '');
                if (isAuth) {
                    setIsAuthenticated(true);
                    // Redirect to dashboard if on login page
                    if (currentPage === Page.ADMIN_LOGIN) {
                        // if (import.meta.env.DEV) console.log("Redirecting to dashboard from onAuthStateChange");
                        onNavigate(Page.ADMIN_DASHBOARD);
                    }
                }
            } else {
                setIsAuthenticated(false);
                setUserEmail(null);
            }
        });

        return () => subscription.unsubscribe();
    }, [currentPage, onNavigate]);

    return (
        <AuthContext.Provider value={{ isAuthenticated, isAuthChecking, userEmail }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
